import React from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { FeatureRequestService } from '@/services/featureRequestService';
import { FeatureRequest } from '@/types/featureRequestTypes';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { ThumbsUp, Clock, RotateCcw, CheckCircle, Lightbulb, ExternalLink } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface FeatureRequestsPreviewProps {
  maxItems?: number;
  showViewAll?: boolean;
  className?: string;
}

export const FeatureRequestsPreview: React.FC<FeatureRequestsPreviewProps> = ({
  maxItems = 3,
  showViewAll = true,
  className
}) => {
  const { user } = useAuth();

  const {
    data: featureRequests = [],
    isLoading
  } = useQuery({
    queryKey: ['featureRequests'],
    queryFn: () => user ? FeatureRequestService.getFeatureRequestsWithVoteStatus(user.id) : [],
    enabled: !!user
  });

  const getStatusBadge = (status: FeatureRequest['status']) => {
    switch (status) {
      case 'planned':
        return (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Planned
          </Badge>
        );
      case 'in-progress':
        return (
          <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
            <RotateCcw className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'implemented':
        return (
          <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Implemented
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-600">
            <Lightbulb className="h-3 w-3 mr-1" />
            New
          </Badge>
        );
    }
  };

  // Get top voted active requests (excluding implemented)
  const topRequests = featureRequests
    .filter(req => req.status !== 'implemented')
    .sort((a, b) => b.votes_count - a.votes_count)
    .slice(0, maxItems);

  const implementedCount = featureRequests.filter(req => req.status === 'implemented').length;
  const totalActiveCount = featureRequests.filter(req => req.status !== 'implemented').length;

  if (isLoading) {
    return (
      <div className={className}>
        <div className="space-y-3">
          {Array.from({ length: maxItems }).map((_, i) => (
            <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/4" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
          ))}
          
          {showViewAll && (
            <div className="flex justify-between items-center pt-3 border-t">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-16" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={className}>
        <div className="text-center py-6 text-gray-500">
          <Lightbulb className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm">Sign in to view and vote on feature requests</p>
        </div>
      </div>
    );
  }

  if (featureRequests.length === 0) {
    return (
      <div className={className}>
        <div className="text-center py-6 text-gray-500">
          <Lightbulb className="mx-auto h-8 w-8 text-gray-400 mb-2" />
          <p className="text-sm mb-3">No feature requests yet</p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/help/feature-requests">Submit the first request</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="space-y-3">
        {topRequests.map(request => (
          <div key={request.id} className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors">
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-medium text-sm leading-tight line-clamp-2">
                  {request.title}
                </p>
                {getStatusBadge(request.status)}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3" />
                  {request.votes_count} votes
                </span>
                <span>
                  {formatDate(request.created_at)}
                </span>
              </div>
            </div>
          </div>
        ))}
        
        {showViewAll && (
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="text-xs text-gray-500 space-y-1">
              <p>{totalActiveCount} active requests</p>
              {implementedCount > 0 && (
                <p className="text-green-600">{implementedCount} implemented</p>
              )}
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/help/feature-requests" className="flex items-center gap-1">
                View All
                <ExternalLink className="h-3 w-3" />
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};